/**
 * BarEditor — the docked side panel for the selected plan bar (mirrors the
 * wireframe's `.bar-editor`): start/end dates, depth from/until, CPE ref, phase.
 * A sibling of the gantt-wrap so it never overlaps the canvas it edits; it
 * stacks below on narrow viewports. Only plan bars are editable — an actual/npt
 * selection shows read-only context. Edits bubble up as partial patches.
 */
import type { TimelineNode, TimelineTrack } from "@api/types";
import { useLocale } from "@hooks/useLocale";

export interface BarEditorProps {
  node: TimelineNode | null;
  tracks: TimelineTrack[];
  onPatch: (patch: Partial<TimelineNode>) => void;
  onDelete: () => void;
  onClose: () => void;
}

export function BarEditor({ node, tracks, onPatch, onDelete, onClose }: BarEditorProps) {
  const { t } = useLocale();
  if (!node) {
    return (
      <aside className="bar-editor" aria-label={t("timeline.editor.title")}>
        <p className="dim" style={{ fontSize: 12.5, margin: 0 }}>
          {t("timeline.editor.hint")}
        </p>
      </aside>
    );
  }

  const editable = node.kind === "plan";
  const track = tracks.find((tr) => tr.id === node.track_id);

  return (
    <aside className="bar-editor open" role="dialog" aria-label={t("timeline.editor.title")}>
      <button type="button" className="close-x" aria-label={t("common.close")} onClick={onClose}>
        ✕
      </button>
      <h4>{node.label || track?.label || node.track_id}</h4>
      <div className="sub">
        {track?.label ?? node.track_id}
        {node.cpe_ref ? ` · ${node.cpe_ref}` : ""} · #{node.seq}
      </div>

      {!editable ? (
        <p className="dim" style={{ fontSize: 12.5 }}>
          {t("timeline.editor.readonly")}
        </p>
      ) : (
        <>
          <div className="frow full">
            <label htmlFor="ed-label">{t("timeline.editor.label")}</label>
            <input
              id="ed-label"
              value={node.label}
              onChange={(e) => onPatch({ label: e.target.value })}
            />
          </div>
          <div className="frow">
            <div>
              <label htmlFor="ed-start">{t("timeline.editor.start")}</label>
              <input
                id="ed-start"
                type="date"
                value={node.start.slice(0, 10)}
                onChange={(e) => onPatch({ start: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="ed-end">{t("timeline.editor.end")}</label>
              <input
                id="ed-end"
                type="date"
                value={node.end?.slice(0, 10) ?? ""}
                onChange={(e) => onPatch({ end: e.target.value || null })}
              />
            </div>
          </div>
          <div className="frow">
            <div>
              <label htmlFor="ed-depth-from">{t("timeline.editor.depthFrom")}</label>
              <input
                id="ed-depth-from"
                type="number"
                inputMode="decimal"
                className="tab-nums"
                value={node.depth_from ?? ""}
                onChange={(e) => onPatch({ depth_from: numOrNull(e.target.value) })}
              />
            </div>
            <div>
              <label htmlFor="ed-depth-to">{t("timeline.editor.depthUntil")}</label>
              <input
                id="ed-depth-to"
                type="number"
                inputMode="decimal"
                className="tab-nums"
                value={node.depth_until ?? ""}
                onChange={(e) => onPatch({ depth_until: numOrNull(e.target.value) })}
              />
            </div>
          </div>
          <div className="frow">
            <div>
              <label htmlFor="ed-cpe">{t("timeline.editor.cpe")}</label>
              <input
                id="ed-cpe"
                value={node.cpe_ref ?? ""}
                onChange={(e) => onPatch({ cpe_ref: e.target.value || null })}
              />
            </div>
            <div>
              <label htmlFor="ed-phase">{t("timeline.editor.phase")}</label>
              <input
                id="ed-phase"
                value={node.phase ?? ""}
                onChange={(e) => onPatch({ phase: e.target.value || null })}
              />
            </div>
          </div>
          <div className="actions">
            <button type="button" className="btn ghost sm danger" onClick={onDelete}>
              {t("timeline.editor.delete")}
            </button>
            <button type="button" className="btn sm" onClick={onClose}>
              {t("common.close")}
            </button>
          </div>
        </>
      )}
    </aside>
  );
}

/** Parse a numeric input to a number-or-null (blank → null, garbage → null). */
function numOrNull(raw: string): number | null {
  const s = raw.trim();
  if (s === "") return null;
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}
